import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowRight, FaUsers, FaRobot, FaPen } from 'react-icons/fa';
import AppStoreBadges from '../components/AppStoreBadges';

const Width = styled.div`
  width: min(1160px, calc(100% - 3rem));
  margin-inline: auto;
  @media (max-width: 480px) { width: calc(100% - 2rem); }
`;
const Hero = styled.section`
  background: var(--color-midnight);
  color: var(--color-cream);
  overflow: hidden;
`;
const HeroGrid = styled(Width)`
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  align-items: center;
  gap: 2rem;
  padding-block: clamp(3rem, 7vw, 6.5rem);
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding-block: 3rem 2rem;
  }
`;
const Eyebrow = styled.p`
  font-size: 0.75rem;
  font-weight: 750;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--color-teal);
  margin-bottom: 1.25rem;
`;
const Title = styled.h1`
  font-size: clamp(3rem, 6.3vw, 5.4rem);
  font-weight: 750;
  line-height: 1.04;
  letter-spacing: -0.055em;
  max-width: 12ch;
  text-wrap: balance;
  span { color: var(--color-teal); }
  @media (max-width: 480px) { font-size: clamp(2.7rem, 12vw, 3.7rem); }
`;
const HeroText = styled.p`
  margin-block: 1.75rem 2rem;
  color: #d9d3e9;
  font-size: 1.125rem;
  max-width: 44ch;
  line-height: 1.75;
`;
const DownloadArea = styled.div`
  scroll-margin-top: 6rem;
  width: fit-content;
  max-width: 100%;
`;
const DownloadNote = styled.p`
  margin-top: 1rem;
  color: #c5bddb;
  font-size: 0.8rem;
`;
const HeroArt = styled.figure`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 0;
  img { width: min(100%, 520px); height: auto; }
  figcaption { margin-top: -0.75rem; color: #c5bddb; font-size: 0.75rem; letter-spacing: 0.05em; }
  @media (max-width: 800px) {
    img { width: min(100%, 360px); }
    figcaption { margin-top: -1.25rem; }
  }
`;
const IntroLine = styled(Width)`
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  padding-block: 1.4rem;
  color: var(--color-text-light);
  font-size: 0.875rem;
  border-bottom: 1px solid var(--border-color);
  span { display: flex; align-items: center; gap: 0.65rem; }
  b { color: var(--color-text); font-weight: 650; }
  @media (max-width: 700px) { flex-wrap: wrap; gap: 0.75rem 1.5rem; }
`;
const Section = styled.section`
  padding-block: clamp(3.5rem, 7vw, 6rem);
`;
const SectionHeading = styled.div`
  max-width: 660px;
  margin-bottom: 2.5rem;
  ${Eyebrow} { color: var(--color-teal-dark); margin-bottom: 0.85rem; }
  h2 { font-size: clamp(2rem, 4vw, 3rem); font-weight: 730; letter-spacing: -0.04em; line-height: 1.15; text-wrap: balance; }
  > p:last-child { margin-top: 1rem; color: var(--color-text-light); max-width: 56ch; }
`;
const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;
const Feature = styled.article`
  padding: 2rem;
  border: 1px solid var(--border-color);
  border-radius: 16px;
  background: #fffaf2;
  > svg { color: var(--color-teal-dark); font-size: 1.5rem; margin-bottom: 2rem; }
  h3 { font-size: 1.35rem; letter-spacing: -0.025em; margin-bottom: 0.85rem; }
  p { color: var(--color-text-light); font-size: 0.95rem; line-height: 1.75; }
  @media (max-width: 760px) { > svg { margin-bottom: 1rem; } }
`;
const RulesSection = styled(Section)`background: #eee8dc;`;
const RulesGrid = styled(Width)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(2.5rem, 7vw, 7rem);
  align-items: center;
  ${SectionHeading} { margin-bottom: 1.5rem; }
  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;
const TextLink = styled(Link)`
  display: inline-flex;
  gap: 0.65rem;
  align-items: center;
  min-height: 44px;
  font-weight: 700;
  color: var(--color-midnight);
  text-decoration: underline;
  text-underline-offset: 5px;
  &:hover { color: var(--color-teal-dark); }
  svg { font-size: 0.8rem; }
`;
const CaptureExample = styled.figure`
  background: var(--color-midnight);
  border-radius: 20px;
  color: var(--color-cream);
  padding: clamp(1.25rem, 4vw, 2.5rem);
  figcaption { font-size: 0.8rem; color: #c5bddb; margin-top: 1.5rem; }
`;
const ExampleLabel = styled.p`
  color: var(--color-teal);
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  font-weight: 750;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
`;
const Equation = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.45rem;
  > span { color: #c5bddb; font-size: 1.3rem; }
  strong { font-size: clamp(1.6rem, 4vw, 2.3rem); color: var(--color-teal); }
`;
const ExampleCard = styled.div<{ $red?: boolean }>`
  width: clamp(48px, 8vw, 78px);
  aspect-ratio: 0.7;
  background: var(--color-cream);
  color: ${({ $red }) => $red ? '#b33139' : '#1c124a'};
  border-radius: 7px;
  padding: 0.4rem 0.55rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-family: Georgia, serif;
  font-size: clamp(1.4rem, 3vw, 2rem);
  font-weight: 700;
  line-height: 1.1;
  small { font-size: 0.8em; align-self: flex-end; }
`;
const GuideHeader = styled.div`
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 2.5rem;
  ${SectionHeading} { margin-bottom: 0; }
  @media (max-width: 760px) { align-items: start; flex-direction: column; }
`;
const GuideLink = styled(Link)`
  display: flex;
  flex-direction: column;
  padding: 1.75rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: #fffaf2;
  color: var(--color-midnight);
  transition: border-color 150ms ease, background-color 150ms ease;
  &:hover { color: var(--color-midnight); border-color: var(--color-teal-dark); background: white; }
  > span { color: var(--color-teal-dark); font-size: 0.7rem; font-weight: 750; text-transform: uppercase; letter-spacing: 0.12em; }
  h3 { font-size: 1.3rem; line-height: 1.35; margin-block: 1.25rem 0.75rem; }
  p { color: var(--color-text-light); font-size: 0.9rem; margin-bottom: 1.5rem; }
  > svg { margin-top: auto; color: var(--color-teal-dark); }
`;
const Closing = styled(Section)`
  background: var(--color-midnight);
  color: var(--color-cream);
  text-align: center;
  h2 { font-size: clamp(2rem, 4.5vw, 3.5rem); letter-spacing: -0.04em; margin-bottom: 1rem; }
  p { color: #d9d3e9; margin-bottom: 2rem; }
`;

const HomePage: React.FC = () => (
  <>
    <Hero>
      <HeroGrid>
        <div>
          <Eyebrow>The Iranian card game. Together again.</Eyebrow>
          <Title>A familiar game.<br />A <span>fresh hand.</span></Title>
          <HeroText>Bring Pasur to your phone. Play online with friends, sharpen your skills against bots, or keep score around a real table.</HeroText>
          <DownloadArea id="download">
            <AppStoreBadges variant="light" size="md" />
            <DownloadNote>For iPhone and Android · Free to download</DownloadNote>
          </DownloadArea>
        </div>
        <HeroArt>
          <img src="/images/pasur-mark.png" width="800" height="800" alt="Pasur: an ace of clubs, two of clubs, and ten of diamonds in a golden capture arc" loading="eager" />
          <figcaption>Good cards. Better company.</figcaption>
        </HeroArt>
      </HeroGrid>
    </Hero>
    <IntroLine aria-label="Ways to enjoy Pasur">
      <span><b>2–4 players</b> Individual online play</span>
      <span><b>Solo practice</b> Play against bots</span>
      <span><b>At the table</b> Keep the score</span>
    </IntroLine>
    <Section>
      <Width>
        <SectionHeading>
          <Eyebrow>Find your way to play</Eyebrow>
          <h2>One game.<br />Room for every kind of player.</h2>
          <p>From a quick practice round to a game with friends, Pasur keeps the cards and the counting close at hand.</p>
        </SectionHeading>
        <FeatureGrid>
          <Feature>
            <FaUsers aria-hidden="true" />
            <h3>Share a table online</h3>
            <p>Invite friends for a game with two, three, or four individual players. Play your cards while the app handles the scoring.</p>
          </Feature>
          <Feature>
            <FaRobot aria-hidden="true" />
            <h3>Make time to practice</h3>
            <p>Play against bots when you want a solo game. Get familiar with captures, special cards, and the choices that shape each hand.</p>
          </Feature>
          <Feature>
            <FaPen aria-hidden="true" />
            <h3>Keep your real table moving</h3>
            <p>Already have a deck and good company? Use the manual scorekeeper to record round scores while you play in person.</p>
          </Feature>
        </FeatureGrid>
      </Width>
    </Section>
    <RulesSection>
      <RulesGrid>
        <div>
          <SectionHeading>
            <Eyebrow>Easy to start. Plenty to discover.</Eyebrow>
            <h2>A little counting.<br />A lot of possibility.</h2>
            <p>Number cards capture table cards when their values, including the card you play, add up to eleven. Aces count as one. Jacks, queens, and kings have their own capture rules.</p>
          </SectionHeading>
          <TextLink to="/rules">Learn how to play <FaArrowRight aria-hidden="true" /></TextLink>
        </div>
        <CaptureExample>
          <ExampleLabel>A number-card capture</ExampleLabel>
          <Equation role="img" aria-label="Play a four to capture a three and a four. Four plus three plus four equals eleven.">
            <ExampleCard aria-hidden="true">4<small>♣</small></ExampleCard>
            <span aria-hidden="true">+</span>
            <ExampleCard $red aria-hidden="true">3<small>♦</small></ExampleCard>
            <span aria-hidden="true">+</span>
            <ExampleCard aria-hidden="true">4<small>♠</small></ExampleCard>
            <span aria-hidden="true">=</span>
            <strong aria-hidden="true">11</strong>
          </Equation>
          <figcaption>Your played 4 takes the 3 and 4 from the table. This is an illustrated example.</figcaption>
        </CaptureExample>
      </RulesGrid>
    </RulesSection>
    <Section>
      <Width>
        <GuideHeader>
          <SectionHeading><Eyebrow>From the Pasur notebook</Eyebrow><h2>A better next hand starts here.</h2></SectionHeading>
          <TextLink to="/blog/">Explore the guides <FaArrowRight aria-hidden="true" /></TextLink>
        </GuideHeader>
        <FeatureGrid>
          <GuideLink to="/blog/how-to-play-pasur/"><span>Start here</span><h3>Your first hand, step by step</h3><p>Get comfortable with the deck, the table, and what happens on your turn.</p><FaArrowRight aria-hidden="true" /></GuideLink>
          <GuideLink to="/blog/pasur-scoring-guide/"><span>Know your points</span><h3>Valuable cards, clubs, and Surs</h3><p>Understand clubs, aces, jacks, the ten of diamonds, and the two of clubs.</p><FaArrowRight aria-hidden="true" /></GuideLink>
          <GuideLink to="/blog/pasur-two-three-four-players/"><span>At the table</span><h3>Pasur with two, three, or four</h3><p>Find practical help for different player counts and agreeing on house rules.</p><FaArrowRight aria-hidden="true" /></GuideLink>
        </FeatureGrid>
      </Width>
    </Section>
    <Closing>
      <Width>
        <Eyebrow>Your next game is a tap away</Eyebrow>
        <h2>Bring the table with you.</h2>
        <p>Download Pasur Online – Play &amp; Score for your phone.</p>
        <AppStoreBadges variant="light" />
      </Width>
    </Closing>
  </>
);
export default HomePage;
